import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useEffect } from "react";
import Layout from "./components/Layout";
import HomePage from "./pages/HomePage";
import FleetPage from "./pages/FleetPage";
import FdmPage from "./pages/FdmPage";
import ApplyPage from "./pages/ApplyPage";
import ContactPage from "./pages/ContactPage";
import PrivacyPage from "./pages/PrivacyPage";
import EtlbPage from "./pages/EtlbPage";
import InterepOverviewPage from "./pages/InterepOverviewPage";
import IntelexHomePage from "./pages/IntelexHomePage";
import IntelexNewReportPage from "./pages/IntelexNewReportPage";
import IntelexReportsPage from "./pages/IntelexReportsPage";
import IntelexReportDetailPage from "./pages/IntelexReportDetailPage";
import IntelexTemplateBuilderPage from "./pages/IntelexTemplateBuilderPage";
import IntelexReviewQueuePage from "./pages/IntelexReviewQueuePage";
import IntelexExamPage from "./pages/IntelexExamPage";
import IntelexExamAdminPage from "./pages/IntelexExamAdminPage";
import InterepAdminSettingsPage from "./pages/InterepAdminSettingsPage";
import InterepTestLoginPage from "./pages/InterepTestLoginPage";
import { ConfirmationDialogHost } from "./intelex/ConfirmationDialog";

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [pathname]);

  return null;
}

function DeploymentUpdateGuard() {
  const { pathname } = useLocation();

  useEffect(() => {
    let stopped = false;
    const check = async () => {
      try {
        const response = await fetch(`/asset-manifest.json?check=${Date.now()}`, {
          cache: "no-store",
          headers: { Accept: "application/json" },
        });
        if (!response.ok || stopped) return;
        const manifest = await response.json();
        const latestMain = manifest?.files?.["main.js"];
        const loadedMain = Array.from(document.scripts)
          .map((script) => script.getAttribute("src") || "")
          .find((source) => /\/static\/js\/main\.[a-f0-9]+\.js(?:$|\?)/.test(source));
        if (
          latestMain &&
          loadedMain &&
          new URL(latestMain, window.location.origin).pathname !==
            new URL(loadedMain, window.location.origin).pathname
        ) {
          window.location.reload();
        }
      } catch (_error) {
        // A failed version check must never interrupt normal navigation.
      }
    };
    const onVisible = () => {
      if (!document.hidden) check();
    };
    check();
    window.addEventListener("pageshow", check);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      stopped = true;
      window.removeEventListener("pageshow", check);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [pathname]);

  return null;
}

function LegacyInterepRedirect() {
  const location = useLocation();
  return (
    <Navigate
      to={`${location.pathname.replace(/^\/intelex/, "/interep")}${location.search}${location.hash}`}
      replace
    />
  );
}

export default function App() {
  return (
    <>
      <ScrollToTop />
      <DeploymentUpdateGuard />
      <ConfirmationDialogHost />
      <Routes>
        <Route path="/interep" element={<IntelexHomePage />} />
        <Route path="/interep/test" element={<InterepTestLoginPage />} />
        <Route
          path="/interep/report/new/:templateId"
          element={<IntelexNewReportPage />}
        />
        <Route path="/interep/reports" element={<IntelexReportsPage />} />
        <Route
          path="/interep/reports/pirep/:pirepId"
          element={<IntelexReportDetailPage />}
        />
        <Route
          path="/interep/reports/reference/:eventNumber"
          element={<IntelexReportDetailPage />}
        />
        <Route
          path="/interep/reports/:reportId"
          element={<IntelexReportDetailPage />}
        />
        <Route
          path="/interep/admin/templates"
          element={<IntelexTemplateBuilderPage />}
        />
        <Route
          path="/interep/admin/reviews"
          element={<IntelexReviewQueuePage />}
        />
        <Route
          path="/interep/admin/responses"
          element={<IntelexReviewQueuePage mode="responses" />}
        />
        <Route path="/interep/exam" element={<IntelexExamPage />} />
        <Route path="/interep/admin/exams" element={<IntelexExamAdminPage />} />
        <Route
          path="/interep/admin/settings"
          element={<InterepAdminSettingsPage />}
        />
        <Route path="/intelex/*" element={<LegacyInterepRedirect />} />
        <Route element={<Layout />}>
          <Route path="/" element={<HomePage />} />
          <Route path="/fleet" element={<FleetPage />} />
          <Route
            path="/fleet.html"
            element={<Navigate to="/fleet" replace />}
          />
          <Route path="/fdm" element={<FdmPage />} />
          <Route path="/fdm.html" element={<Navigate to="/fdm" replace />} />
          <Route path="/etlb" element={<EtlbPage />} />
          <Route path="/etlb.html" element={<Navigate to="/etlb" replace />} />
          <Route path="/interep-overview" element={<InterepOverviewPage />} />
          <Route
            path="/interep-overview.html"
            element={<Navigate to="/interep-overview" replace />}
          />
          <Route path="/apply" element={<ApplyPage />} />
          <Route
            path="/apply.html"
            element={<Navigate to="/apply" replace />}
          />
          <Route path="/contact" element={<ContactPage />} />
          <Route
            path="/contact.html"
            element={<Navigate to="/contact" replace />}
          />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route
            path="/privacy.html"
            element={<Navigate to="/privacy" replace />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </>
  );
}
